import { createRoot } from "react-dom/client";
import "../bootstrap";
import { retireServerShell } from "./shipping-shell";

const COMPONENT_LOADERS = {
	"homepage-widget-shipping-tool": async () => {
		const [{ HomepageBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool/homepage"),
		]);

		return HomepageBootstrap;
	},
	"basic-shipping-tool": async () => {
		const [{ BasicShippingToolBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool"),
		]);

		return BasicShippingToolBootstrap;
	},
	"quick-shipping-tool": async () => {
		const [{ QuickShippingToolBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool"),
		]);

		return QuickShippingToolBootstrap;
	},
	"shipping-tool-cart": async () => {
		const [{ CartBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool"),
		]);

		return CartBootstrap;
	},
	"shipping-tool-thank-you": async () => {
		const [{ ThankYouBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool"),
		]);

		return ThankYouBootstrap;
	},
	"shipment-quick-starter": async () => {
		const [{ ShipmentQuickStarterBootstrap }] = await Promise.all([
			import("@shipitfi/shipping-tool"),
		]);

		return ShipmentQuickStarterBootstrap;
	},
};

// Store mounted components in a WeakSet to allow garbage collection
const mountedComponents = new WeakSet();
const mountingComponents = new WeakSet();

const parseProps = (dataset) =>
	Object.entries(dataset).reduce((acc, [key, value]) => {
		if (key === "shippingToolInitData") {
			return { ...acc, ...JSON.parse(value) };
		}
		return {
			...acc,
			[key.endsWith("Json") ? key.slice(0, -4) : key]: key.endsWith("Json")
				? JSON.parse(value)
				: value,
		};
	}, {});

const renderComponent = (element, props, Component) => {
	element.innerHTML = "";

	createRoot(element).render(<Component {...props} />);
	retireServerShell(element);
};

const mountComponent = async (element) => {
	if (
		!element ||
		mountedComponents.has(element) ||
		mountingComponents.has(element) ||
		!COMPONENT_LOADERS[element.id]
	) {
		return;
	}

	try {
		mountingComponents.add(element);
		const rawProps = parseProps(element.dataset);
		const Component = await COMPONENT_LOADERS[element.id]();
		renderComponent(element, rawProps, Component);
		mountedComponents.add(element);
		mountingComponents.delete(element);
	} catch (error) {
		mountingComponents.delete(element);
		console.error(`Error mounting component ${element.id}:`, error);
	}
};

const handleMutations = (mutations) => {
	mutations.forEach((mutation) => {
		// Handle attribute modifications
		if (
			mutation.type === "attributes" &&
			mutation.attributeName === "data-shipping-tool" &&
			mutation.target.hasAttribute("data-shipping-tool")
		) {
			mountComponent(mutation.target);
			return;
		}

		// Handle new nodes
		Array.from(mutation.addedNodes)
			.filter(
				(node) =>
					node.nodeType === 1 &&
					node.hasAttribute &&
					node.hasAttribute("data-shipping-tool"),
			)
			.forEach(mountComponent);
	});
};

const createObserver = () => {
	const observer = new MutationObserver(handleMutations);
	observer.observe(document.documentElement, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["data-shipping-tool"],
	});

	return observer;
};

const periodicCheck = (checkCount = 0, maxChecks = 10) => {
	if (checkCount >= maxChecks) {
		return;
	}

	const unmountedExists = Array.from(
		document.querySelectorAll("[data-shipping-tool]"),
	).some((component) => {
		if (!mountedComponents.has(component)) {
			mountComponent(component);

			return true;
		}

		return false;
	});

	if (unmountedExists) {
		setTimeout(() => periodicCheck(checkCount + 1, maxChecks), 100);
	}
};

const initialize = () => {
	// Mount initial components
	document.querySelectorAll("[data-shipping-tool]").forEach(mountComponent);

	const observer = createObserver();
	periodicCheck();

	return observer;
};

const cleanup = (observer) => {
	observer?.disconnect();
	// No need to clear mountedComponents as it's a WeakSet
};

const start = () => {
	let observer = initialize();

	// Handle navigation
	window.addEventListener("popstate", () => {
		cleanup(observer);

		observer = initialize();
	});

	// Cleanup function for module unload
	return () => cleanup(observer);
};

// Initialize on DOM ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", start);
} else {
	start();
}
